/**
 * Client-side Gmail-like category + spam heuristics.
 * Pure frontend — no backend dependency. Safe defaults: prefer "primary".
 */

const PROMO_RE = /\b(promo|oferta|descuento|sale|% off|newsletter|unsubscribe|baja|marketing|cup[oó]n|black friday|cyber|deal|rebaja|outlet|catalogo|catálogo)\b/i;
const SOCIAL_RE = /\b(linkedin|facebook|twitter|x\.com|instagram|tiktok|youtube|github|discord|slack|telegram|whatsapp|notificaci[oó]n de|te mencion|mentioned you|friend request|conexi[oó]n|accepted your)\b/i;
const SPAM_RE = /\b(viagra|crypto\s*airdrop|nigerian|wire transfer|account.?suspended|verify your account|click here now|lottery|winner|prize|free money|bitcoin giveaway|urgent action required|password expire)\b/i;
const UPDATE_RE = /\b(factura|invoice|receipt|pedido|order #|shipment|env[ií]o|tracking|security alert|c[oó]digo de verificaci[oó]n|2fa|otp|password reset|restablecer)\b/i;

const PROMO_DOMAINS = /\b(mailchimp|sendgrid|klaviyo|constantcontact|hubspot|emarketing|newsletter|promo\.|deals\.|offers\.)/i;
const SOCIAL_DOMAINS = /\b(linkedin\.com|facebookmail|twitter\.com|x\.com|instagram|github\.com|discord|slack\.com|redditmail)\b/i;

function blob(m) {
  return [
    m.subject || '',
    m.from?.email || '',
    m.from?.name || '',
    m.preview || m.snippet || '',
    m.accountEmail || '',
  ].join(' ');
}

/** @returns {'primary'|'promotions'|'social'|'updates'} */
export function classifyCategory(m) {
  // Prefer server-side cached category when present (Phase B)
  if (m?.category && ['primary', 'promotions', 'social', 'updates'].includes(m.category)) {
    return m.category;
  }
  const t = blob(m);
  const email = (m.from?.email || '').toLowerCase();
  if (SOCIAL_RE.test(t) || SOCIAL_DOMAINS.test(email)) return 'social';
  if (PROMO_RE.test(t) || PROMO_DOMAINS.test(email)) return 'promotions';
  if (UPDATE_RE.test(t)) return 'updates'; // treat as primary for tab UI (Gmail has Updates tab in some locales; we map to primary)
  return 'primary';
}

/**
 * Lightweight spam score 0–100 for badges (not a substitute for server spam folder).
 * @returns {{ score: number, likely: boolean, reasons: string[] }}
 */
export function spamHeuristic(m) {
  if (typeof m?.spam_score === 'number') {
    return {
      score: m.spam_score,
      likely: m.spam_likely != null ? !!m.spam_likely : m.spam_score >= 40,
      reasons: m.spam_reasons || [],
    };
  }
  const t = blob(m);
  const email = (m.from?.email || '').toLowerCase();
  let score = 0;
  const reasons = [];

  if (SPAM_RE.test(t)) { score += 45; reasons.push('palabras sospechosas'); }
  if (/\$\$|!!!|\b100%\s*gratis\b/i.test(t)) { score += 15; reasons.push('énfasis comercial agresivo'); }
  if (email && !email.includes('@')) { score += 20; reasons.push('remitente inválido'); }
  // No-reply mass senders slightly up if promo-ish
  if (/noreply|no-reply|donotreply/i.test(email) && PROMO_RE.test(t)) { score += 10; reasons.push('remitente masivo'); }
  // Random-looking local part
  if (/^[a-z0-9]{18,}@/i.test(email)) { score += 12; reasons.push('dirección inusual'); }

  score = Math.min(100, score);
  return { score, likely: score >= 40, reasons };
}

export function filterByCategory(messages, tab) {
  if (!tab || tab === 'primary') {
    // Primary = not promotions and not social (Gmail-like)
    return messages.filter((m) => {
      const c = classifyCategory(m);
      return c === 'primary' || c === 'updates';
    });
  }
  if (tab === 'promotions') return messages.filter((m) => classifyCategory(m) === 'promotions');
  if (tab === 'social') return messages.filter((m) => classifyCategory(m) === 'social');
  return messages;
}

export function countByCategory(messages) {
  const out = { primary: 0, promotions: 0, social: 0 };
  for (const m of messages) {
    const c = classifyCategory(m);
    if (c === 'social') out.social += 1;
    else if (c === 'promotions') out.promotions += 1;
    else out.primary += 1;
  }
  return out;
}
